<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4 shadow-sm border-0">
          <!-- Card Header -->
          <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h6 class="font-weight-bolder mb-0" style="color: #252f40 !important;">Counter Management</h6>
              <p class="text-xs text-secondary mb-0">Manage service windows and assigned attendants</p>
            </div>
            
            <!-- ADD COUNTER BUTTON: HIDDEN IF USER LACKS CREATE PERMISSION -->
            <button v-if="canAddCounter" class="btn btn-sm bg-gradient-success mb-0 w-100 w-md-auto shadow-sm" @click="openModal()">
              <i class="fas fa-plus me-2 text-xs"></i>Add Counter
            </button>
          </div>

          <!-- Table Body / Counter Records in Modern Card Grid Format -->
          <div class="card-body px-4 pt-4 pb-4">
            <div class="row g-4" v-if="filteredCounters.length > 0">
              <div 
                v-for="item in filteredCounters" 
                :key="item.id" 
                class="col-12 col-md-6 col-lg-4 col-xl-3 d-flex"
              >
                <!-- Modern Station Card -->
                <div class="counter-card-modern w-100 shadow-sm">
                  <!-- Status Accent Bar on Top -->
                  <div 
                    class="card-top-bar" 
                    :class="(item.status === 'published' || item.status === 'Active') ? 'bg-gradient-success' : 'bg-gradient-secondary'"
                  ></div>

                  <!-- Card Inner Container -->
                  <div class="card-inner-content d-flex flex-column justify-content-between h-100 p-3">
                    <div>
                      <!-- Card Header: Icon, Name, and Status Badge -->
                      <div class="d-flex justify-content-between align-items-start mb-3">
                        <div class="d-flex align-items-center gap-2">
                          <div class="counter-icon-glow">
                            <i class="fas fa-tv text-white text-xs"></i>
                          </div>
                          <div>
                            <h6 class="mb-0 text-sm font-weight-bolder text-dark">{{ item.name }}</h6>
                            <!-- DISPLAY BRANCH NAME -->
                            <p class="text-xxs text-secondary mb-0">
                              <i class="fas fa-map-marker-alt me-1 text-primary opacity-8"></i>
                              {{ item.branch ? item.branch.branch_name : 'N/A' }}
                            </p>
                          </div>
                        </div>

                        <!-- Live Status Pill with Pulsing Dot -->
                        <span 
                          class="status-indicator-pill" 
                          :class="(item.status === 'published' || item.status === 'Active') ? 'status-active' : 'status-inactive'"
                        >
                          <span class="status-dot"></span>
                          {{ item.status }}
                        </span>
                      </div>

                      <!-- Attendant Info Row (User Profile Badge Style) -->
                      <div class="attendant-box p-2 rounded-3 mb-3 d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center gap-2">
                          <div class="attendant-avatar">
                            <i class="fas fa-user-tie text-secondary text-xxs"></i>
                          </div>
                          <div>
                            <span class="text-xxs text-muted d-block line-height-1">Attendant</span>
                            <span class="text-xs font-weight-bold text-dark">
                              {{ item.user ? item.user.name : (item.attendant_name || 'N/A') }}
                            </span>
                          </div>
                        </div>
                        <span class="badge bg-white text-secondary text-xxs border shadow-none px-2 py-1">Desk Staff</span>
                      </div>


                      <!-- DISPLAY MULTIPLE SERVICE NAMES WITH WRAPPING -->
                      <div class="mb-2">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                          <!-- SERVICE TYPE COLUMN - UPDATED TO PREVENT OVERFLOW -->
                          <span class="text-uppercase text-secondary text-xxs font-weight-bolder">Assigned Services</span>
                          <span class="badge bg-light text-secondary text-xxs font-weight-bold">
                            {{ getCounterServices(item).length }}
                          </span>
                        </div>

                        <div class="services-inline-list">
                          <span v-for="srv in getCounterServices(item)" :key="srv.id" class="badge-service-pill">
                            <i class="fas fa-tag me-1 opacity-6"></i>{{ srv.name }}
                          </span>
                          <span v-if="getCounterServices(item).length === 0" class="text-xxs text-secondary italic">
                            No service assigned
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- ACTION COLUMN: HIDES EDIT/DELETE IF ROLE HAS NO PERMISSION -->
                    <div class="card-actions-footer pt-3 mt-2 border-top d-flex justify-content-end align-items-center gap-2">
                      <button 
                        v-if="canEditCounter" 
                        @click="editCounter(item)" 
                        class="btn-action-pill btn-action-edit" 
                        title="Edit Counter"
                      >
                        <i class="fas fa-pencil-alt me-1"></i> Edit
                      </button>
                      <button 
                        v-if="canDeleteCounter" 
                        @click="deleteCounter(item.id)" 
                        class="btn-action-pill btn-action-delete" 
                        title="Delete Counter"
                      >
                        <i class="far fa-trash-alt me-1"></i> Delete
                      </button>
                      <span v-if="!canEditCounter && !canDeleteCounter" class="text-xxs text-secondary font-weight-bold py-1">
                        -
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="filteredCounters.length === 0" class="text-center py-5">
              <div class="empty-state-icon mb-3">
                <i class="fas fa-desktop text-secondary opacity-3 fa-2x"></i>
              </div>
              <p class="text-secondary font-weight-bold mb-0">No counters available.</p>
              <p class="text-xs text-muted">Click "Add Counter" above to create your first counter window.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL OVERLAY -->
    <div v-if="showModal" class="custom-modal-backdrop px-3">
      <div class="card shadow-xl custom-modal-card w-100 border-0">
        <div class="card-header pb-0 d-flex justify-content-between align-items-center bg-white border-0">
          <h6 class="font-weight-bolder mb-0" style="color: #252f40 !important;">{{ editMode ? 'Update' : 'Add New' }} Counter</h6>
          <div class="close-circle" @click="closeModal">
            <i class="fas fa-times"></i>
          </div>
        </div>
        <div class="card-body pt-3">
          <form @submit.prevent="saveCounter">
            <!-- 1st FIELD: SELECT BRANCH -->
            <div class="mb-3">
              <label class="form-label text-uppercase text-xxs font-weight-bolder text-secondary ms-1">Select Branch</label>
              <select v-model="form.branch_id" class="form-select custom-input" required>
                <option :value="null" disabled>Choose a branch</option>
                <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                  {{ branch.branch_name }}
                </option>
              </select>
            </div>

            <!-- SERVICE MULTI-SELECT FIELD: MODERN CHIPS DESIGN -->
            <div class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label text-uppercase text-xxs font-weight-bolder text-secondary ms-1 mb-0">Service Types (Multiple)</label>
                <button 
                  type="button" 
                  class="btn btn-link p-0 text-xxs font-weight-bold text-primary mb-0 text-decoration-none" 
                  @click="toggleSelectAllServices"
                  v-if="servicesList.length > 0"
                >
                  <i class="fas" :class="isAllServicesSelected ? 'fa-minus-circle me-1' : 'fa-check-double me-1'"></i>
                  {{ isAllServicesSelected ? 'Deselect All' : 'Select All' }}
                </button>
              </div>
              <div class="services-grid-container p-2 border rounded bg-light-soft">
                <!-- SELECT ALL CHIP -->
                <div v-if="servicesList.length > 0" class="service-chip-item">
                  <input 
                    type="checkbox" 
                    id="srv-select-all" 
                    :checked="isAllServicesSelected"
                    @change="toggleSelectAllServices"
                    class="d-none"
                  >
                  <label for="srv-select-all" class="service-chip-label select-all-chip">
                    <span class="chip-prefix">ALL</span>
                    <span class="chip-name font-weight-bold">Select All</span>
                    <i class="fas fa-check-circle chip-check"></i>
                  </label>
                </div>
                <div v-for="srv in servicesList" :key="srv.id" class="service-chip-item">
                  <input 
                    type="checkbox" 
                    :id="'srv-' + srv.id" 
                    :value="srv.id" 
                    v-model="form.service_ids"
                    class="d-none"
                  >
                  <label :for="'srv-' + srv.id" class="service-chip-label">
                    <span class="chip-prefix">{{ srv.prefix }}</span>
                    <span class="chip-name">{{ srv.name }}</span>
                    <i class="fas fa-check-circle chip-check"></i>
                  </label>
                </div>
                <div v-if="servicesList.length === 0" class="text-center py-3 text-secondary text-xs italic">
                  No services available.
                </div>
              </div>
              <div class="d-flex justify-content-between mt-1 px-1">
                <small class="text-xxs text-muted italic">Click to select/deselect items.</small>
                <small class="text-xxs font-weight-bold text-success">{{ form.service_ids.length }} Selected</small>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label text-uppercase text-xxs font-weight-bolder text-secondary ms-1">Counter Name</label>
              <input v-model="form.name" type="text" class="form-control custom-input" placeholder="e.g. Cash Counter 01" required />
            </div>

            <!-- ATTENDANT NAME FIELD: SEARCHABLE DROPDOWN -->
            <div class="mb-3 position-relative">
              <label class="form-label text-uppercase text-xxs font-weight-bolder text-secondary ms-1">Attendant Name</label>
              
              <div class="input-group custom-input-group">
                <span class="input-group-text bg-transparent border-0 pe-0"><i class="fas fa-search text-xs text-secondary"></i></span>
                <input 
                  type="text" 
                  class="form-control border-0 px-3 bg-transparent text-sm" 
                  placeholder="Search person..."
                  v-model="attendantSearch"
                  @focus="showAttendantDropdown = true"
                  @input="showAttendantDropdown = true"
                  required
                />
                <span class="input-group-text bg-transparent border-0 cursor-pointer" @click="showAttendantDropdown = !showAttendantDropdown">
                  <i class="fas fa-chevron-down text-xxs text-secondary"></i>
                </span>
              </div>

              <!-- FLOATING SEARCH RESULTS -->
              <div v-if="showAttendantDropdown" class="search-dropdown-list shadow-lg border rounded-3 bg-white w-100">
                <div 
                  v-for="att in filteredAttendants" 
                  :key="att.id" 
                  class="search-item p-3"
                  @click="selectAttendant(att)"
                >
                  <div class="d-flex align-items-center">
                    <div class="avatar-circle-xs bg-gradient-light me-2">
                      <i class="fas fa-user text-xxs text-secondary"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 text-xs text-dark">{{ att.name }}</h6>
                      <p class="text-xxs text-secondary mb-0">{{ att.role ? att.role.name : 'Staff' }}</p>
                    </div>
                  </div>
                </div>
                <div v-if="filteredAttendants.length === 0" class="p-3 text-center text-xs text-secondary italic">
                  No matches found
                </div>
              </div>
            </div>

            <!-- STATUS FIELD: RADIO STYLE -->
            <div class="mb-4">
              <label class="form-label text-uppercase text-xxs font-weight-bolder text-secondary ms-1">Status</label>
              <div class="d-flex gap-4 ms-1">
                <div class="form-check">
                  <input class="form-check-input" type="radio" value="Active" id="statusActive" v-model="form.status">
                  <label class="custom-control-label text-xs mb-0 cursor-pointer" for="statusActive">Active</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" value="Inactive" id="statusInactive" v-model="form.status">
                  <label class="custom-control-label text-xs mb-0 cursor-pointer" for="statusInactive">Inactive</label>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4 pt-2">
              <button type="button" class="btn btn-sm btn-light border mb-0 px-4" @click="closeModal">Cancel</button>
              <button type="submit" class="btn btn-sm bg-gradient-success mb-0 px-4 shadow-sm">Save Counter</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "Counter",
  data() {
    return {
      counters: [],
      attendants: [], 
      branches: [],
      servicesList: [],
      showModal: false,
      editMode: false,
      userRole: "",
      userPermissions: [],
      attendantSearch: "",
      showAttendantDropdown: false,
      // form stores service_ids as an array
      form: { id: null, name: "", user_id: null, branch_id: null, service_ids: [], attendant_name: "", status: "Active" },
      apiUrl: `${process.env.VUE_APP_API_BASE_URL}/v1/admin/counter`,
      attendantApiUrl: `${process.env.VUE_APP_API_BASE_URL}/v1/admin/user/attendants`,
      branchApiUrl: `${process.env.VUE_APP_API_BASE_URL}/v1/admin/branches`,
      servicesApiUrl: `${process.env.VUE_APP_API_BASE_URL}/v1/admin/services/list`
    };
  },
  computed: {
    isSuperAdmin() {
      const role = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/\s+/g, '');
      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }
      return role.includes('superadmin') || user.type === 1 || user.role === 'SuperAdmin' || user.role_name === 'SuperAdmin';
    },
    // DYNAMIC PERMISSION CHECK: Respects dynamically assigned permissions for all roles (including Doctor, Token Staff, etc.)
    canAddCounter() {
      const role = this.getCleanRole();
      if (['superadmin', 'admin', 'supervisor'].includes(role)) return true;
      return this.hasPermission('create');
    },
    // DYNAMIC PERMISSION CHECK: Respects update permission for all roles
    canEditCounter() {
      const role = this.getCleanRole();
      if (['superadmin', 'admin', 'supervisor'].includes(role)) return true;
      return this.hasPermission('update');
    },
    // DYNAMIC PERMISSION CHECK: Respects delete permission for all roles
    canDeleteCounter() {
      const role = this.getCleanRole();
      if (['superadmin', 'admin', 'supervisor'].includes(role)) return true;
      return this.hasPermission('delete');
    },
    canManageCounters() {
      return this.canAddCounter || this.canEditCounter || this.canDeleteCounter;
    },
    isDoctor() {
      if (!this.userRole) return false;
      const cleaned = String(this.userRole).toLowerCase().replace(/[^a-z0-9]/gi, '');
      return cleaned === 'doctor' || cleaned.includes('doctor');
    },
    filteredCounters() {
      if (!Array.isArray(this.counters)) return [];

      const currentRole = String(this.userRole || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

      const getCounterUserRole = (item) => {
        let u = item.user;
        let r = '';
        if (u) {
          if (u.role_name) r = String(u.role_name);
          else if (u.role_slug) r = String(u.role_slug);
          else if (u.roleName) r = String(u.roleName);
          else if (u.role) {
            if (typeof u.role === 'string') r = u.role;
            else if (typeof u.role === 'object') r = u.role.name || u.role.role_name || u.role.slug || '';
          }
        }
        return String(r).toLowerCase().replace(/[^a-z0-9]/gi, '');
      };

      // 1. DOCTOR ROLE FILTER: Show ONLY counters assigned to / managed by Doctors
      if (currentRole.includes('doctor') || this.isDoctor) {
        const currentName = String(localStorage.getItem('userName') || localStorage.getItem('name') || '').toLowerCase();
        const currentId = localStorage.getItem('id');

        return this.counters.filter(item => {
          const r = getCounterUserRole(item);
          const attName = String(item.user?.name || item.attendant_name || '').toLowerCase();
          
          const isDoc = r.includes('doctor') || attName.startsWith('dr') || attName.includes('doctor');
          const isSelf = (currentName && attName.includes(currentName)) || (currentId && item.user_id == currentId);
          
          return isDoc || isSelf;
        });
      }

      // 2. TOKENUSER & TOKENMANAGER ROLE FILTER: Hide Doctor counters
      if (currentRole.includes('tokenuser') || currentRole.includes('tokenmanager') || currentRole.includes('tokenmanger')) {
        return this.counters.filter(item => {
          const r = getCounterUserRole(item);
          const attName = String(item.user?.name || item.attendant_name || '').toLowerCase();
          const isDoc = r.includes('doctor') || attName.startsWith('dr') || attName.includes('doctor');
          return !isDoc;
        });
      }

      return this.counters;
    },
    filteredAttendants() {
      if (!Array.isArray(this.attendants)) return [];

      const getRole = (a) => {
        if (!a) return '';
        let r = '';
        if (a.role_name) r = String(a.role_name);
        else if (a.role_slug) r = String(a.role_slug);
        else if (a.roleName) r = String(a.roleName);
        else if (a.roleSlug) r = String(a.roleSlug);
        else if (a.user_role) r = String(a.user_role);
        else if (a.userRole) r = String(a.userRole);
        else if (a.role_type) r = String(a.role_type);
        else if (a.role) {
          if (typeof a.role === 'string') r = a.role;
          else if (typeof a.role === 'object') {
            r = a.role.name || a.role.role_name || a.role.slug || a.role.title || '';
          }
        } else if (a.Role) {
          if (typeof a.Role === 'string') r = a.Role;
          else if (typeof a.Role === 'object') {
            r = a.Role.name || a.Role.role_name || a.Role.slug || a.Role.title || '';
          }
        }
        return String(r).toLowerCase().replace(/[^a-z0-9]/gi, '');
      };

      const currentRole = String(this.userRole || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

      let list = this.attendants;

      // 1. DOCTOR ROLE FILTER: Show ONLY doctors in Attendant Name dropdown
      if (currentRole.includes('doctor') || this.isDoctor) {
        list = this.attendants.filter(a => {
          const r = getRole(a);
          const name = String(a.name || '').toLowerCase();
          return r.includes('doctor') || name.startsWith('dr') || name.includes('doctor');
        });

        const currentName = localStorage.getItem('userName') || localStorage.getItem('name');
        const currentId = localStorage.getItem('id');
        if (currentName) {
          const exists = list.some(d => String(d.name).toLowerCase() === String(currentName).toLowerCase() || (currentId && d.id == currentId));
          if (!exists) {
            list.unshift({ id: currentId || '', name: currentName, role_name: 'doctor' });
          }
        }
      }
      // 2. TOKENUSER & TOKENMANAGER ROLE FILTER: Show ONLY token staff (exclude Doctors)
      else if (currentRole.includes('tokenuser') || currentRole.includes('tokenmanager') || currentRole.includes('tokenmanger')) {
        list = this.attendants.filter(a => {
          const r = getRole(a);
          const name = String(a.name || '').toLowerCase();
          const isDoc = r.includes('doctor') || name.startsWith('dr') || name.includes('doctor');
          if (isDoc) return false;
          return r.includes('tokenuser') || r.includes('tokenmanager') || r.includes('tokenmanger') || r.includes('operator') || r.includes('staff') || r === 'user' || r === '';
        });
      }

      if (!this.attendantSearch) return list;
      return list.filter(att => 
        String(att.name || '').toLowerCase().includes(this.attendantSearch.toLowerCase())
      );
    },
    isAllServicesSelected() {
      if (!this.servicesList || this.servicesList.length === 0) return false;
      return this.servicesList.length === this.form.service_ids.length;
    }
  },
  methods: {
    getCleanRole() {
      const r = this.getUserRole();
      return String(r || '').toLowerCase().replace(/['"]+/g, '').replace(/\s+/g, '');
    },
    getUserPermissions() {
      let perms = [];

      // 1. Check direct keys in localStorage
      const keys = ['userModules', 'allowedModules', 'permissions', 'modules', 'role_permissions'];
      for (const k of keys) {
        const val = localStorage.getItem(k);
        if (val) {
          try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed) && parsed.length > 0) {
              perms = parsed;
              break;
            }
          } catch (e) {
            if (val.includes(',')) {
              perms = val.split(',').map(s => s.trim());
              break;
            }
          }
        }
      }

      // 2. Check user/profile object
      if (perms.length === 0) {
        const userKeys = ['user', 'profile', 'userData'];
        for (const uk of userKeys) {
          const uStr = localStorage.getItem(uk);
          if (uStr) {
            try {
              const u = JSON.parse(uStr);
              let p = u.permissions || (u.role && u.role.permissions) || (u.user && u.user.role && u.user.role.permissions) || u.modules || (u.role && u.role.modules);
              if (typeof p === 'string') {
                try { p = JSON.parse(p); } catch (e) {
                  console.error("Error parsing stringified permissions:", e);
                }
              }
              if (Array.isArray(p) && p.length > 0) {
                perms = p;
                break;
              }
            } catch (e) {
              console.error("Error parsing user profile data:", e);
            }
          }
        }
      }

      return perms.map(item => {
        if (typeof item === 'string') return item.toLowerCase().trim();
        if (typeof item === 'number') return String(item);
        if (typeof item === 'object' && item !== null) {
          return String(item.name || item.module_name || item.code || '').toLowerCase().trim();
        }
        return '';
      }).filter(Boolean);
    },
    // DYNAMIC SCOPE EVALUATOR: Recognizes module variations (counter, countermanagement, counters) and action verbs
    hasPermission(action) {
      const role = this.getCleanRole();
      if (['superadmin', 'admin', 'supervisor'].includes(role)) return true;

      const perms = this.userPermissions;
      if (!Array.isArray(perms) || perms.length === 0) return false;

      const targetAction = String(action || '').toLowerCase().trim();
      const validCounterPrefixes = ['counter', 'countermanagement', 'counters'];

      return perms.some(p => {
        const permStr = String(p).toLowerCase().trim();
        if (!permStr) return false;

        // 1. Direct module or legacy numeric identifier grant
        if (validCounterPrefixes.includes(permStr) || permStr === '21') {
          return true;
        }

        // 2. Action scope format (e.g., "countermanagement:create", "counter:update", "counter:*")
        if (permStr.includes(':')) {
          const [modPart, actPart] = permStr.split(':').map(s => s.trim());
          if (validCounterPrefixes.includes(modPart)) {
            if (actPart === '*' || actPart === 'all') return true;
            if (actPart === targetAction) return true;

            // Synonym mapping for CRUD verbs
            if (targetAction === 'create' && (actPart === 'add' || actPart === 'post')) return true;
            if (targetAction === 'update' && (actPart === 'edit' || actPart === 'put')) return true;
            if (targetAction === 'delete' && (actPart === 'remove' || actPart === 'destroy')) return true;
            if (targetAction === 'read' && (actPart === 'view' || actPart === 'list' || actPart === 'get')) return true;
          }
        }

        return false;
      });
    },
    toggleSelectAllServices() {
      if (this.isAllServicesSelected) {
        this.form.service_ids = [];
      } else {
        this.form.service_ids = this.servicesList.map(srv => srv.id);
      }
    },
    // Maps the service_ids string/array from the counter object to actual service objects for the UI
    getCounterServices(item) {
      if (!item || !item.service_ids) return [];
      try {
        let selectedIds = item.service_ids;

        // Handle stringified JSON or double-encoded JSON
        if (typeof selectedIds === 'string') {
          try {
            selectedIds = JSON.parse(selectedIds);
          } catch (e) {
            selectedIds = selectedIds.split(',').map(id => id.trim());
          }
        }
        if (typeof selectedIds === 'string') {
          try {
            selectedIds = JSON.parse(selectedIds);
          } catch (e) { /* ignore */ }
        }

        if (!Array.isArray(selectedIds)) return [];

        // Uniformly convert all IDs to Numbers to eliminate string vs integer mismatches
        const numericSelectedIds = selectedIds.map(id => Number(id));

        return this.servicesList.filter(srv => numericSelectedIds.includes(Number(srv.id)));
      } catch (e) {
        return [];
      }
    },
    selectAttendant(att) {
      this.form.user_id = att.id;
      this.form.attendant_name = att.name;
      this.attendantSearch = att.name;
      this.showAttendantDropdown = false;
    },
    getUserRole() {
      let role = localStorage.getItem('role') || 
                 localStorage.getItem('user_role') || 
                 localStorage.getItem('userRole') ||
                 localStorage.getItem('type');

      if (!role) {
        const userData = localStorage.getItem('user') || localStorage.getItem('profile');
        if (userData) {
          try {
            const parsed = JSON.parse(userData);
            role = parsed.role || (parsed.user && parsed.user.role) || parsed.type || (parsed.data && parsed.data.role);
          } catch (e) { role = userData; }
        }
      }
      return role || "";
    },
    getAuthHeaders() {
      const token = localStorage.getItem('token');
      return { headers: { 'Authorization': `Bearer ${token}` } };
    },
    async fetchCounters() {
      try {
        const response = await axios.get(this.apiUrl, this.getAuthHeaders());
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          this.counters = response.data.data;
        }
      } catch (error) { console.error("Fetch error:", error); }
    },
    async fetchAttendants() {
      try {
        const response = await axios.get(this.attendantApiUrl, this.getAuthHeaders());
        if (response.data && response.data.success) {
          this.attendants = response.data.data;
        }
      } catch (error) { console.error("Error fetching attendants:", error); }
    },
    async fetchBranches() {
      try {
        const response = await axios.get(this.branchApiUrl, this.getAuthHeaders());
        if (response.data) {
          this.branches = response.data;
        }
      } catch (error) { console.error("Error fetching branches:", error); }
    },
    async fetchServices() {
      try {
        const response = await axios.get(this.servicesApiUrl, this.getAuthHeaders());
        if (response.data && response.data.success) {
          this.servicesList = response.data.data;
        }
      } catch (error) { console.error("Error fetching services:", error); }
    },
    openModal() {
      if (!this.canAddCounter) return;
      this.editMode = false;
      this.form = { id: null, name: "", user_id: null, branch_id: null, service_ids: [], attendant_name: "", status: "Active" };
      this.attendantSearch = "";
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.showAttendantDropdown = false;
    },
    editCounter(item) {
      if (!this.canEditCounter) return;
      this.editMode = true;
      let parsedIds = [];
      try {
        parsedIds = item.service_ids ? (typeof item.service_ids === 'string' ? JSON.parse(item.service_ids) : item.service_ids) : [];
      } catch (e) { parsedIds = []; }

      this.form = { 
        ...item, 
        user_id: item.user_id || (item.user ? item.user.id : null),
        branch_id: item.branch_id || null,
        service_ids: parsedIds,
        attendant_name: item.user ? item.user.name : (item.attendant_name || "") 
      };
      this.attendantSearch = this.form.attendant_name;
      this.showModal = true;
    },
    async saveCounter() {
      try {
        if (this.editMode ? !this.canEditCounter : !this.canAddCounter) {
          return;
        }
        const url = this.editMode ? `${this.apiUrl}/update/${this.form.id}` : `${this.apiUrl}/add`;
        await axios[this.editMode ? 'put' : 'post'](url, this.form, this.getAuthHeaders());
        this.closeModal();
        this.fetchCounters();
      } catch (error) { 
        console.error(error);
        alert(error.response?.data?.message || "Server Error"); 
      }
    },
    async deleteCounter(id) {
      if (!this.canDeleteCounter) return;
      if (confirm("Are you sure?")) {
        try {
          await axios.delete(`${this.apiUrl}/delete/${id}`, this.getAuthHeaders());
          this.fetchCounters();
        } catch (error) { console.error(error); }
      }
    },
    handleOutsideClick(e) {
      if (!this.$el.querySelector('.position-relative')?.contains(e.target)) {
        this.showAttendantDropdown = false;
      }
    },
    // Synchronize session permissions dynamically if updated across tabs or saved in RoleTable
    syncSessionData() {
      this.userRole = this.getUserRole();
      this.userPermissions = this.getUserPermissions();
    }
  },
  created() {
    this.userRole = this.getUserRole();
    this.userPermissions = this.getUserPermissions();
  },
  mounted() {
    this.fetchCounters();
    this.fetchAttendants(); 
    this.fetchBranches();
    this.fetchServices();
    document.addEventListener("mousedown", this.handleOutsideClick);
    window.addEventListener("storage", this.syncSessionData);
  },
  unmounted() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    window.removeEventListener("storage", this.syncSessionData);
  }
};
</script>

<style scoped>
/* Modal Base Styling */
.custom-modal-backdrop {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background-color: rgba(30, 40, 60, 0.4); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center; z-index: 1050;
}
.custom-modal-card { max-width: 500px; border-radius: 1.25rem; overflow: hidden; background: white !important; }

/* Modal Header Close Button */
.close-circle {
  width: 32px; height: 32px; background: #f8f9fa; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; 
  cursor: pointer; transition: all 0.2s; color: #67748e;
}
.close-circle:hover { background: #fee2e2; color: #f5365c; }

/* Input Styling */
.custom-input, .custom-input-group {
  border: 1px solid #d2d6da; border-radius: 0.5rem; transition: border-color 0.15s ease-in-out;
  padding: 0.5rem 0.75rem; font-size: 0.875rem;
}
.custom-input-group { display: flex; align-items: center; padding: 0; }
.custom-input:focus, .custom-input-group:focus-within {
  border-color: #2dce89; box-shadow: 0 0 0 2px rgba(45, 206, 137, 0.2); outline: 0;
}

/* Service Chips Design */
.services-grid-container {
  display: flex; flex-wrap: wrap; gap: 8px; 
  max-height: 180px; overflow-y: auto; padding: 10px; border-radius: 0.75rem;
}
.bg-light-soft { background-color: #fcfdfe; }
.service-chip-label {
  display: flex; align-items: center; gap: 8px; padding: 6px 12px;
  background: #fff; border: 1px solid #e9ecef; border-radius: 50px;
  cursor: pointer; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 0; user-select: none;
}
.select-all-chip {
  background-color: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
}
.chip-prefix { color: #8392ab; font-size: 10px; font-weight: 800; border-right: 1px solid #eee; padding-right: 8px; }
.chip-name { font-size: 11px; font-weight: 600; color: #344767; }
.chip-check { font-size: 0.8rem; color: #2dce89; opacity: 0; transform: scale(0); transition: all 0.2s; }

/* Chip Selected State */
input[type="checkbox"]:checked + .service-chip-label {
  background: #e6fcf5; border-color: #2dce89; transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}
input[type="checkbox"]:checked + .service-chip-label .chip-prefix { color: #2dce89; border-color: rgba(45, 206, 137, 0.3); }
input[type="checkbox"]:checked + .service-chip-label .chip-check { opacity: 1; transform: scale(1); }

/* Table Service Pill Styling - Prevent Expansion */
.services-inline-list {
  display: flex; flex-wrap: wrap; gap: 6px; max-width: 100%; align-items: center;
}
.badge-service-pill {
  display: inline-flex; align-items: center; background-color: #f0f7ff; color: #0284c7;
  font-size: 10px; font-weight: 700; padding: 4px 8px; border-radius: 6px;
  text-transform: uppercase; white-space: nowrap; border: 1px solid #bae6fd;
  max-width: 140px; overflow: hidden; text-overflow: ellipsis;
}

/* Attendant Dropdown Styling */
.search-dropdown-list {
  position: absolute; top: 100%; left: 0; right: 0; max-height: 240px; 
  overflow-y: auto; z-index: 1100; margin-top: 5px;
}
.search-item { cursor: pointer; transition: background 0.2s; border-bottom: 1px solid #f8f9fa; }
.search-item:hover { background-color: #f0f2f5; }
.avatar-circle-xs {
  width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
}

/* ==============================================
   NEW MODERN STATION CARD DESIGN
   ============================================== */
.counter-card-modern {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
}

.counter-card-modern:hover {
  transform: translateY(-4px);
  border-color: #cbd5e1;
  box-shadow: 0 12px 24px -4px rgba(15, 23, 42, 0.08) !important;
}

/* Top colored accent bar */
.card-top-bar {
  height: 4px;
  width: 100%;
}

/* Counter Icon with Gradient */
.counter-icon-glow {
  width: 36px;
  height: 36px;
  min-width: 36px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

/* Status Pill with Pulsing Live Dot */
.status-indicator-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-active {
  background-color: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.status-inactive {
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.status-active .status-dot {
  background-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
}

.status-inactive .status-dot {
  background-color: #94a3b8;
}

/* Attendant Box */
.attendant-box {
  background: #f8fafc;
  border: 1px solid #edf2f7;
}

.attendant-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.line-height-1 {
  line-height: 1;
}

/* Action Buttons in Footer */
.btn-action-pill {
  border: none;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: transparent;
}

.btn-action-edit {
  color: #0284c7;
  background: #f0f9ff;
}

.btn-action-edit:hover {
  background: #0284c7;
  color: #ffffff;
}

.btn-action-delete {
  color: #e11d48;
  background: #fff1f2;
}

.btn-action-delete:hover {
  background: #e11d48;
  color: #ffffff;
}

.italic { font-style: italic; }
.cursor-pointer { cursor: pointer; }
</style>